/// <amd-module name="Repos/Changes/Components/ChangesSummaryViewer" />
import * as React from "react";
import { ChangeSummaryCard } from "Repos/Changes/Components/ChangeSummaryCard";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { IDiffSummaryDiscussionIntegration, IDiffSummaryDiscussionWidget } from "Repos/Discussion/Common/DiscussionContracts";
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { IIntersectionContext, IntersectionContext } from "VSSUI/Intersection";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";

export interface IChangesSummaryViewerProps {
    changes: IChange[];
    compareVersion: string;
    diffIntegrations?: IDiffSummaryDiscussionIntegration[];
    folderPath: string;
    rootDiffIntegration?: IDiffSummaryDiscussionIntegration;
    highlightText?: string;
    getFileUrl?: (path: string) => string;
    onViewFile?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, change: IChange) => void;
}

export class ChangesSummaryViewer extends VssComponent<IChangesSummaryViewerProps> {
    private intersectionContext: IIntersectionContext;
    private loadingElement: HTMLDivElement | null;
    private maxChanges = new ObservableValue(25);

    public componentDidMount() {
        if (this.intersectionContext) {
            this.intersectionContext.register(this.onIntersect);
        }
    }

    public componentWillUnmount() {
        if (this.intersectionContext) {
            this.intersectionContext.unregister(this.onIntersect);
        }
    }

    public render() {
        const { changes, compareVersion, diffIntegrations, folderPath, getFileUrl, highlightText, onViewFile, rootDiffIntegration } = this.props;

        return (
            <>
                {rootDiffIntegration && (
                    <Observer widgets={rootDiffIntegration.getWidgets()}>
                        {(props: { widgets: IDiffSummaryDiscussionWidget[] }) => (props.widgets.length > 0 ? props.widgets[0].render!() : null)}
                    </Observer>
                )}
                <Observer maxChanges={this.maxChanges}>
                    {({ maxChanges }: { maxChanges: number }) => {
                        let changesToShow = changes;
                        if (changes.length > maxChanges) {
                            changesToShow = changes.slice(0, maxChanges);
                        }
                        return (
                            <>
                                {changesToShow.map((change: IChange, index: number) => {
                                    return (
                                        <ChangeSummaryCard
                                            key={`${folderPath}.${change.path}`}
                                            change={change}
                                            compareVersion={compareVersion}
                                            diffIntegration={diffIntegrations ? diffIntegrations[index] : undefined}
                                            highlightText={highlightText}
                                            fileUrl={getFileUrl ? getFileUrl(change.path) : undefined}
                                            onViewFile={
                                                onViewFile
                                                    ? event => {
                                                          onViewFile(event, change);
                                                      }
                                                    : undefined
                                            }
                                        />
                                    );
                                })}
                                {changes.length > maxChanges && (
                                    <IntersectionContext.Consumer>
                                        {intersectionContext => {
                                            this.intersectionContext = intersectionContext;
                                            return (
                                                <div className="flex-grow flex-column padding-16" ref={element => this.observeElement(element)}>
                                                    <Spinner />
                                                </div>
                                            );
                                        }}
                                    </IntersectionContext.Consumer>
                                )}
                            </>
                        );
                    }}
                </Observer>
            </>
        );
    }

    private observeElement = (element: HTMLDivElement | null) => {
        if (this.intersectionContext) {
            if (this.loadingElement) {
                this.intersectionContext.unobserve(this.loadingElement);
            }
            if (element) {
                this.loadingElement = element;
                this.intersectionContext.observe(element);
            }
        }
    };

    private onIntersect = (entries: IntersectionObserverEntry[]) => {
        for (const entry of entries) {
            if (entry.target === this.loadingElement && entry.isIntersecting) {
                this.intersectionContext.unobserve(this.loadingElement);
                this.loadingElement = null;
                this.maxChanges.value = this.maxChanges.value * 2;
            }
        }
    };
}
